import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
    RefreshCw, Search, X, Eye, Download, ChevronUp, ChevronDown,
    ChevronLeft, ChevronRight, Phone, Mail, MapPin, User, Tag,
    TrendingUp, Users, Zap, Calendar, Clock, AlertCircle, Loader2,
    Filter, BarChart2, Settings2, CheckSquare, Square, ExternalLink,
    Building2, Globe, Megaphone, SlidersHorizontal,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

/* ──────────────────────────────────────────
   CONSTANTS
────────────────────────────────────────── */
const STATUS_COLORS = {
    'New': 'bg-blue-100 text-blue-800 ring-blue-200',
    'Contacted': 'bg-purple-100 text-purple-800 ring-purple-200',
    'Not Contacted': 'bg-slate-100 text-slate-700 ring-slate-200',
    'Converted': 'bg-emerald-100 text-emerald-800 ring-emerald-200',
    'Lost Lead': 'bg-red-100 text-red-700 ring-red-200',
    'Pre-Qualified': 'bg-amber-100 text-amber-800 ring-amber-200',
    'Not Qualified': 'bg-rose-100 text-rose-700 ring-rose-200',
};

/* Field groups — prefix-match against Zoho API field names */
const FIELD_GROUPS = [
    { id: 'identity', label: 'Identity', icon: User, prefixes: ['First_Name', 'Last_Name', 'Full_Name', 'Salutation', 'Email', 'Mobile', 'Phone', 'Skype'] },
    { id: 'location', label: 'Location', icon: MapPin, prefixes: ['City', 'State', 'Street', 'Zip', 'Country', 'Pincode', 'Business_Address'] },
    { id: 'lead', label: 'Lead Info', icon: Tag, prefixes: ['Lead_Status', 'Lead_Source', 'Lead_Stage', 'Lead_Temperature', 'Lead_Type', 'Lead_From', 'Hot_Leads', 'EBG_PP_Lead_Status'] },
    { id: 'investment', label: 'Investment', icon: TrendingUp, prefixes: ['Investment', 'Daewoo_Investment', 'Your_Investment', 'Desired_Investment', 'When_are_you', 'When_would', 'When_can', 'Planning_to', 'open_to_investing'] },
    { id: 'business', label: 'Business', icon: Building2, prefixes: ['Company', 'Business', 'Type_of_Business', 'Daewoo_Type', 'Industry', 'Years_in_Business', 'Annual', 'Firm_turnover', 'Do_you_currently_own', 'Do_you_have_access', 'Do_you_have_prior'] },
    { id: 'utm', label: 'UTM / Source', icon: Globe, prefixes: ['UTM_', 'Lead_Source', 'Where_did', 'How_did', 'How_do_you_hear', 'Facebook', 'Form_Type', 'EBG_Brand'] },
    { id: 'experience', label: 'Experience', icon: Megaphone, prefixes: ['Experience', 'Franchise', 'Describe_your', 'Detail_your', 'If_yes_please', 'Any_prior', 'Do_you_have_any_prior', 'Do_you_have_experience'] },
    { id: 'meta', label: 'Meta', icon: Clock, prefixes: ['Created_Time', 'Modified_Time', 'Last_Activity', 'Owner', 'Created_By', 'Modified_By', 'Tag', 'Layout', 'Re_created', 'Application_created', 'Token_Date'] },
];

/* Fields always shown in the table (default columns) */
const DEFAULT_COLUMNS = ['fullName', 'phone', 'state', 'status', 'source', 'When_are_you_planning_to_invest', 'Daewoo_Investment_Budget1', 'createdAt'];

/** Zoho Tag for leads belonging to the AGEON website. */
const AGEON_TAG = 'Ageon_EBG';

/* ──────────────────────────────────────────
   HELPERS
────────────────────────────────────────── */
const statusColor = (s) => STATUS_COLORS[s] || 'bg-gray-100 text-gray-600 ring-gray-200';

const fmt = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const fmtTime = (d) => d
    ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

/** Convert a Zoho API_Name to a readable label */
const apiToLabel = (key) =>
    key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').trim();

/** Get the display value for a lead field (handles nested _raw) */
const getVal = (lead, key) => {
    if (key in lead && key !== '_raw') return lead[key] ?? '—';
    return lead._raw?.[key] ?? '—';
};

/** Which group does a field belong to? */
const groupFor = (fieldName) => {
    for (const g of FIELD_GROUPS) {
        if (g.prefixes.some(p => fieldName.startsWith(p))) return g.id;
    }
    return 'other';
};

/** Export visible leads as CSV */
const exportCSV = (leads, columns) => {
    const headers = columns.map(c => apiToLabel(c));
    const rows = leads.map(l => columns.map(c => {
        const v = getVal(l, c);
        return typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v ?? '');
    }));
    const csv = [headers, ...rows]
        .map(r => r.map(v => `"${v.replace(/"/g, '""')}"`).join(','))
        .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `zoho-leads-${Date.now()}.csv`;
    a.click();
};

/* ──────────────────────────────────────────
   SUB-COMPONENTS
────────────────────────────────────────── */

/** Single stat tile */
function StatCard({ icon: Icon, label, value, sub, accent = 'teal', loading }) {
    const accents = {
        teal: 'bg-brand-teal-50   text-brand-teal-700',
        amber: 'bg-amber-50  text-amber-700',
        green: 'bg-emerald-50 text-emerald-700',
        purple: 'bg-purple-50 text-purple-700',
    };
    return (
        <div className="ag-card p-4 flex items-start gap-3">
            <div className={`p-2.5 rounded-xl shrink-0 ${accents[accent]}`}>
                <Icon className="h-5 w-5" />
            </div>
            <div>
                <div className="text-xs text-ink-500 font-medium">{label}</div>
                {loading
                    ? <div className="h-7 w-14 bg-ink-100 rounded animate-pulse mt-1" />
                    : <div className="text-2xl font-extrabold tracking-tight">{value ?? '—'}</div>}
                {sub && <div className="text-[11px] text-ink-400 mt-0.5">{sub}</div>}
            </div>
        </div>
    );
}

/** Column selector modal */
function ColumnPicker({ allKeys, columns, onChange, onClose }) {
    const [draft, setDraft] = useState(new Set(columns));
    const toggle = (k) => setDraft(prev => {
        const n = new Set(prev);
        n.has(k) ? n.delete(k) : n.add(k);
        return n;
    });
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col z-10">
                <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100">
                    <div className="font-bold">Choose columns</div>
                    <button onClick={onClose} className="p-1.5 hover:bg-ink-100 rounded-lg transition"><X className="h-4 w-4" /></button>
                </div>
                <div className="overflow-y-auto flex-1 px-4 py-3 space-y-1">
                    {allKeys.map(k => (
                        <label key={k} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-ink-50 cursor-pointer transition">
                            {draft.has(k)
                                ? <CheckSquare className="h-4 w-4 text-brand-teal-700 shrink-0" />
                                : <Square className="h-4 w-4 text-ink-400 shrink-0" />}
                            <span className="text-sm">{apiToLabel(k)}</span>
                            <span className="ml-auto text-[10px] text-ink-400 font-mono">{k}</span>
                            <input type="checkbox" className="sr-only" checked={draft.has(k)} onChange={() => toggle(k)} />
                        </label>
                    ))}
                </div>
                <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2">
                    <button className="ag-btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="ag-btn-primary" onClick={() => { onChange([...draft]); onClose(); }}>Apply</button>
                </div>
            </div>
        </div>
    );
}

/** Export modal — choose all data or a custom Created/Transfer date range,
 *  then download a server-built .xlsx of the full matching dataset. */
function ExportModal({ onClose, onExportCurrentCSV }) {
    const [scope, setScope] = useState('all');          // 'all' | 'custom'
    const [createdFrom, setCreatedFrom] = useState('');
    const [createdTo, setCreatedTo] = useState('');
    const [transferFrom, setTransferFrom] = useState('');
    const [transferTo, setTransferTo] = useState('');
    const [busy, setBusy] = useState(false);

    const download = async () => {
        setBusy(true);
        try {
            const params = new URLSearchParams();
            if (scope === 'all') {
                params.set('all', 'true');
            } else {
                if (createdFrom) params.set('createdFrom', createdFrom);
                if (createdTo) params.set('createdTo', createdTo);
                if (transferFrom) params.set('transferFrom', transferFrom);
                if (transferTo) params.set('transferTo', transferTo);
                if (!createdFrom && !createdTo && !transferFrom && !transferTo) {
                    toast.error('Pick at least one date, or choose "All data".');
                    setBusy(false);
                    return;
                }
            }

            const res = await api.get(`/zoho/leads/export?${params}`, {
                responseType: 'blob',
                timeout: 120_000,
            });
            const blob = new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `ageon-zoho-leads-${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            URL.revokeObjectURL(a.href);
            toast.success('Excel download started');
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Export failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/30" onClick={busy ? undefined : onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10">
                <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100">
                    <div className="font-bold flex items-center gap-2">
                        <Download className="h-4 w-4 text-brand-teal-700" /> Export to Excel
                    </div>
                    <button onClick={onClose} disabled={busy} className="p-1.5 hover:bg-ink-100 rounded-lg transition disabled:opacity-40">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-4">
                    {/* Scope choice */}
                    <div className="grid grid-cols-2 gap-2">
                        {[
                            { id: 'all', label: 'All data', sub: 'Every AGEON lead' },
                            { id: 'custom', label: 'Custom date', sub: 'Filter by date range' },
                        ].map(opt => (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => setScope(opt.id)}
                                className={`text-left rounded-xl border p-3 transition
                  ${scope === opt.id ? 'border-brand-teal-600 bg-brand-teal-50 ring-1 ring-brand-teal-600' : 'border-ink-200 hover:border-brand-teal-400'}`}
                            >
                                <div className="text-sm font-bold text-ink-800">{opt.label}</div>
                                <div className="text-[11px] text-ink-500 mt-0.5">{opt.sub}</div>
                            </button>
                        ))}
                    </div>

                    {/* Custom date pickers */}
                    {scope === 'custom' && (
                        <div className="space-y-4 pt-1">
                            <div>
                                <div className="flex items-center gap-1.5 mb-1.5 text-xs font-bold uppercase tracking-wider text-ink-500">
                                    <Calendar className="h-3.5 w-3.5" /> Created date
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="ag-label">From</label>
                                        <input type="date" className="ag-input" value={createdFrom} max={createdTo || undefined} onChange={e => setCreatedFrom(e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="ag-label">To</label>
                                        <input type="date" className="ag-input" value={createdTo} min={createdFrom || undefined} onChange={e => setCreatedTo(e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center gap-1.5 mb-1.5 text-xs font-bold uppercase tracking-wider text-ink-500">
                                    <Calendar className="h-3.5 w-3.5" /> Transfer date
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="ag-label">From</label>
                                        <input type="date" className="ag-input" value={transferFrom} max={transferTo || undefined} onChange={e => setTransferFrom(e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="ag-label">To</label>
                                        <input type="date" className="ag-input" value={transferTo} min={transferFrom || undefined} onChange={e => setTransferTo(e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            <p className="text-[11px] text-ink-400 leading-relaxed">
                                Both filters apply together — e.g. leads created in one range that were also transferred in another. Leave a section blank to ignore it.
                            </p>
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-ink-100 flex items-center justify-between gap-2">
                    <button
                        className="text-xs text-ink-500 hover:text-ink-800 underline"
                        onClick={() => { onExportCurrentCSV?.(); onClose(); }}
                        disabled={busy}
                    >
                        Export current page (CSV)
                    </button>
                    <div className="flex gap-2">
                        <button className="ag-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
                        <button className="ag-btn-primary" onClick={download} disabled={busy}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                            {busy ? 'Building…' : 'Download Excel'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Full lead detail drawer — renders all non-null fields grouped */
function LeadDrawer({ lead, onClose }) {
    if (!lead) return null;

    const raw = lead._raw || {};

    // Group all raw fields
    const grouped = useMemo(() => {
        const groups = {};
        for (const g of FIELD_GROUPS) groups[g.id] = [];
        groups['other'] = [];

        for (const [key, val] of Object.entries(raw)) {
            if (!val && val !== 0 && val !== false) continue;
            const gid = groupFor(key);
            groups[gid].push({ key, val });
        }
        return groups;
    }, [raw]);

    return (
        <>
            <div className="fixed inset-0 bg-black/25 z-40" onClick={onClose} />
            <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white z-50 shadow-2xl flex flex-col">
                {/* Header */}
                <div className="px-5 py-4 border-b border-ink-100 flex items-start gap-3">
                    <div className="h-11 w-11 rounded-full bg-brand-teal-50 text-brand-teal-700 font-extrabold text-lg grid place-items-center shrink-0 uppercase">
                        {lead.fullName?.[0] || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-lg leading-tight">{lead.fullName}</div>
                        <div className="text-xs text-ink-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" /> {[lead.city, lead.state].filter(Boolean).join(', ') || 'No location'}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-ink-100 rounded-lg transition shrink-0">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Status + tags */}
                <div className="px-5 py-2.5 border-b border-ink-100 flex flex-wrap gap-1.5">
                    {lead.status && (
                        <span className={`ag-chip text-xs ring-1 ${statusColor(lead.status)}`}>{lead.status}</span>
                    )}
                    {raw.Lead_Temperature && (
                        <span className="ag-chip text-xs bg-amber-50 text-amber-800 ring-1 ring-amber-200">{raw.Lead_Temperature}</span>
                    )}
                    {raw.Tag && (
                        <span className="ag-chip text-xs bg-ink-100 text-ink-600">{raw.Tag}</span>
                    )}
                </div>

                {/* Body — grouped fields */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                    {FIELD_GROUPS.filter(g => (grouped[g.id]?.length || 0) > 0).map(g => (
                        <FieldGroup key={g.id} icon={g.icon} label={g.label} fields={grouped[g.id]} />
                    ))}
                    {grouped.other?.length > 0 && (
                        <FieldGroup icon={SlidersHorizontal} label="Other fields" fields={grouped.other} />
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-ink-100 flex gap-2">
                    {lead.email && (
                        <a href={`mailto:${lead.email}`} className="ag-btn-primary flex-1 justify-center text-sm">
                            <Mail className="h-4 w-4" /> Email
                        </a>
                    )}
                    {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="ag-btn-ghost flex-1 justify-center text-sm">
                            <Phone className="h-4 w-4" /> Call
                        </a>
                    )}
                </div>
            </div>
        </>
    );
}

function FieldGroup({ icon: Icon, label, fields }) {
    const [open, setOpen] = useState(true);
    return (
        <div>
            <button
                className="flex items-center gap-2 w-full mb-2 group"
                onClick={() => setOpen(o => !o)}
            >
                <div className="p-1 rounded-md bg-ink-100 group-hover:bg-ink-200 transition">
                    <Icon className="h-3.5 w-3.5 text-ink-600" />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-ink-500">{label}</span>
                <span className="text-[11px] text-ink-400">({fields.length})</span>
                <ChevronDown className={`h-3.5 w-3.5 text-ink-400 ml-auto transition-transform ${open ? '' : '-rotate-90'}`} />
            </button>
            {open && (
                <div className="grid grid-cols-1 gap-y-1 pl-1">
                    {fields.map(({ key, val }) => (
                        <div key={key} className="flex items-start gap-2 py-1 border-b border-ink-50 last:border-0">
                            <div className="text-[11px] text-ink-400 w-44 shrink-0 pt-0.5 leading-snug">{apiToLabel(key)}</div>
                            <div className="text-sm text-ink-800 font-medium flex-1 break-words">
                                {typeof val === 'boolean' ? (val ? '✓ Yes' : '✗ No') : String(val)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Sortable table header */
function SortTh({ children, field, sort, sortDir, onSort, className = '' }) {
    const active = sort === field;
    return (
        <th
            onClick={() => onSort(field)}
            className={`text-left text-xs font-semibold text-ink-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer select-none whitespace-nowrap hover:text-ink-800 transition ${className}`}
        >
            <span className="inline-flex items-center gap-1">
                {children}
                <span className="flex flex-col -gap-1">
                    <ChevronUp className={`h-2.5 w-2.5 ${active && sortDir === 'asc' ? 'text-brand-teal-700' : 'text-ink-300'}`} />
                    <ChevronDown className={`h-2.5 w-2.5 ${active && sortDir === 'desc' ? 'text-brand-teal-700' : 'text-ink-300'}`} />
                </span>
            </span>
        </th>
    );
}

/* ──────────────────────────────────────────
   MAIN PAGE
────────────────────────────────────────── */
export default function ZohoLeads() {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [error, setError] = useState('');
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [showPicker, setShowPicker] = useState(false);
    const [showExport, setShowExport] = useState(false);

    /* Filters */
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [source, setSource] = useState('');
    const [sort, setSort] = useState('Created_Time');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);

    /* Dynamic columns — keys in the lead/_raw object */
    const [columns, setColumns] = useState(DEFAULT_COLUMNS);

    /* Debounce ref */
    const searchTimer = useRef(null);

    /* All unique field keys across all fetched leads */
    const allKeys = useMemo(() => {
        const standard = ['fullName', 'email', 'phone', 'company', 'state', 'city', 'status', 'source', 'owner', 'createdAt'];
        const raw = leads.flatMap(l => Object.keys(l._raw || {}));
        return [...new Set([...standard, ...raw])].filter(k => k !== 'id' && k !== '_raw');
    }, [leads]);

    /* ── Loaders ── */
    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const { data } = await api.get(`/zoho/leads/stats?tag=${encodeURIComponent(AGEON_TAG)}`);
            setStats(data);
        } catch { }
        finally { setStatsLoading(false); }
    }, []);

    const loadLeads = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page, perPage, sort, sortDir });
            if (search) params.set('search', search);
            if (status) params.set('status', status);
            if (source) params.set('source', source);

            const { data } = await api.get(`/zoho/leads?${params}`);
            setLeads(data.leads || []);
            setTotal(data.total || 0);
            setHasMore(!!data.hasMore);
        } catch (e) {
            const msg = e?.response?.data?.error || 'Failed to load leads';
            setError(msg);
            if (!silent) toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [page, perPage, sort, sortDir, search, status, source]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadLeads(); }, [loadLeads]);

    useEffect(() => {
        if (!autoRefresh) return;
        const id = setInterval(() => loadLeads({ silent: true }), 30_000);
        return () => clearInterval(id);
    }, [autoRefresh, loadLeads]);

    const handleSort = (field) => {
        if (sort === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSort(field); setSortDir('desc'); }
        setPage(1);
    };

    const handleSearch = (v) => {
        setSearch(v);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => setPage(1), 400);
    };

    /* Status distribution */
    const statusEntries = Object.entries(stats?.byStatus || {}).sort((a, b) => b[1] - a[1]);
    const totalPages = Math.ceil(total / perPage) || 1;

    /* Render a cell value cleanly */
    const renderCell = (lead, key) => {
        const val = getVal(lead, key);
        if (val === '—' || val === null || val === undefined) return <span className="text-ink-300">—</span>;
        if (key === 'status') return <span className={`ag-chip text-xs ring-1 ${statusColor(val)}`}>{val}</span>;
        if (key === 'createdAt' || key === 'updatedAt') return <span className="text-xs text-ink-500">{fmt(val)}</span>;
        if (key === 'email') return <a href={`mailto:${val}`} onClick={e => e.stopPropagation()} className="text-xs text-brand-teal-700 hover:underline">{val}</a>;
        if (key === 'phone') return <a href={`tel:${val}`} onClick={e => e.stopPropagation()} className="text-xs text-brand-teal-700 hover:underline">{val}</a>;
        if (key === 'fullName') return (
            <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-full bg-brand-teal-50 text-brand-teal-700 font-bold text-xs grid place-items-center shrink-0 uppercase">
                    {String(val)[0]}
                </div>
                <span className="font-semibold text-ink-800 truncate max-w-[140px]">{val}</span>
            </div>
        );
        // Truncate long values
        const str = String(val);
        return <span className="text-xs text-ink-700 truncate max-w-[160px] block" title={str}>{str}</span>;
    };

    return (
        <div className="space-y-4">

            {/* ── Header ── */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-extrabold">Zoho CRM Leads</h1>
                    <p className="text-sm text-ink-500">
                        {total > 0 ? `${total.toLocaleString()} AGEON leads · ` : ''}
                        Tag: {AGEON_TAG} · Live sync · {columns.length} columns shown
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="flex items-center gap-1.5 text-xs text-ink-600 cursor-pointer select-none">
                        <span className={`w-8 h-4 rounded-full relative transition ${autoRefresh ? 'bg-brand-teal' : 'bg-ink-300'}`}>
                            <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition ${autoRefresh ? 'left-[18px]' : 'left-0.5'}`} />
                            <input type="checkbox" className="sr-only" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
                        </span>
                        Auto-refresh
                    </label>
                    <button className="ag-btn-ghost" onClick={() => { loadLeads(); loadStats(); }} disabled={loading}>
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                    <button className="ag-btn-ghost" onClick={() => setShowPicker(true)}>
                        <Settings2 className="h-4 w-4" /> Columns
                    </button>
                    <button className="ag-btn-outline" onClick={() => setShowExport(true)}>
                        <Download className="h-4 w-4" /> Export Excel
                    </button>
                </div>
            </div>

            {/* ── Stats ── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard icon={Users} label="Total leads" value={stats?.total?.toLocaleString()} accent="teal" loading={statsLoading} sub="All time" />
                <StatCard icon={Zap} label="New today" value={stats?.todayNew?.toLocaleString()} accent="amber" loading={statsLoading} sub="Created today" />
                <StatCard icon={TrendingUp} label="Converted" value={stats?.converted?.toLocaleString()} accent="green" loading={statsLoading} sub="Status = Converted" />
                <StatCard icon={Phone} label="Contacted" value={stats?.contacted?.toLocaleString()} accent="purple" loading={statsLoading} sub="Status = Contacted" />
            </div>

            {/* ── Status pills ── */}
            {statusEntries.length > 0 && (
                <div className="ag-card p-3 flex flex-wrap gap-2 items-center">
                    <BarChart2 className="h-4 w-4 text-ink-400 shrink-0" />
                    {statusEntries.map(([s, count]) => (
                        <button
                            key={s}
                            onClick={() => { setStatus(status === s ? '' : s); setPage(1); }}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition
                ${status === s ? 'border-brand-teal-700 bg-brand-teal-50 text-brand-teal-700' : 'border-ink-200 text-ink-600 hover:border-brand-teal-400'}`}
                        >
                            <span className={`h-2 w-2 rounded-full ${statusColor(s).split(' ')[0]}`} />
                            {s} <span className="opacity-60">({count})</span>
                        </button>
                    ))}
                    {status && (
                        <button onClick={() => { setStatus(''); setPage(1); }} className="text-xs text-ink-400 hover:text-ink-700 underline ml-1">
                            Clear
                        </button>
                    )}
                </div>
            )}

            {/* ── Filters ── */}
            <div className="ag-card p-4">
                <div className="flex flex-wrap gap-2 items-end">
                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                        <label className="ag-label">Search</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                            <input
                                className="ag-input pl-9 pr-8"
                                placeholder="Name, email, phone, company…"
                                value={search}
                                onChange={e => handleSearch(e.target.value)}
                            />
                            {search && (
                                <button className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                                    onClick={() => { setSearch(''); setPage(1); }}>
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="min-w-[130px]">
                        <label className="ag-label">Status</label>
                        <select className="ag-input" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
                            <option value="">All statuses</option>
                            {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>

                    <div className="min-w-[100px]">
                        <label className="ag-label">Per page</label>
                        <select className="ag-input" value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
                            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>

                    <div className="min-w-[140px]">
                        <label className="ag-label">Sort by</label>
                        <select className="ag-input" value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
                            <option value="Created_Time">Date created</option>
                            <option value="Modified_Time">Last modified</option>
                            <option value="Last_Name">Name</option>
                            <option value="Lead_Status">Status</option>
                        </select>
                    </div>

                    {(search || status || source) && (
                        <button className="ag-btn-ghost self-end" onClick={() => { setSearch(''); setStatus(''); setSource(''); setPage(1); }}>
                            <Filter className="h-4 w-4" /> Clear
                        </button>
                    )}
                </div>
            </div>

            {/* ── Error ── */}
            {error && (
                <div className="ag-card p-4 bg-red-50 ring-1 ring-red-200 flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm font-bold text-red-800">Failed to load leads</div>
                        <div className="text-xs text-red-600 mt-0.5">{error}</div>
                    </div>
                    <button className="ag-btn-ghost text-red-700" onClick={() => loadLeads()}>Retry</button>
                </div>
            )}

            {/* ── Dynamic Table ── */}
            <div className="ag-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[600px]">
                        <thead className="bg-ink-50 border-b border-ink-100">
                            <tr>
                                {columns.map((col, i) => (
                                    <SortTh
                                        key={col}
                                        field={col}
                                        sort={sort}
                                        sortDir={sortDir}
                                        onSort={handleSort}
                                        className={i === 0 ? 'pl-4' : ''}
                                    >
                                        {apiToLabel(col)}
                                    </SortTh>
                                ))}
                                <th className="px-3 py-2.5 w-10" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-ink-50">
                            {loading
                                ? Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        {columns.map((_, j) => (
                                            <td key={j} className="px-3 py-3">
                                                <div className="h-4 bg-ink-100 rounded" style={{ width: `${50 + ((i + j) * 13) % 40}%` }} />
                                            </td>
                                        ))}
                                        <td className="px-3 py-3"><div className="h-4 w-8 bg-ink-100 rounded" /></td>
                                    </tr>
                                ))
                                : leads.length === 0
                                    ? (
                                        <tr>
                                            <td colSpan={columns.length + 1} className="text-center py-16">
                                                <Users className="h-10 w-10 mx-auto mb-3 text-ink-200" />
                                                <div className="text-ink-500 font-medium">No leads found</div>
                                                <div className="text-xs text-ink-400 mt-1">Try changing your search or filters</div>
                                            </td>
                                        </tr>
                                    )
                                    : leads.map(lead => (
                                        <tr
                                            key={lead.id}
                                            className="hover:bg-brand-teal-50/40 transition cursor-pointer group"
                                            onClick={() => setSelected(lead)}
                                        >
                                            {columns.map((col, i) => (
                                                <td key={col} className={`px-3 py-2.5 max-w-[200px] ${i === 0 ? 'pl-4' : ''}`}>
                                                    {renderCell(lead, col)}
                                                </td>
                                            ))}
                                            <td className="px-2 py-2.5">
                                                <button
                                                    className="opacity-0 group-hover:opacity-100 transition ag-btn-ghost py-1 px-2 text-xs"
                                                    onClick={e => { e.stopPropagation(); setSelected(lead); }}
                                                >
                                                    <Eye className="h-3.5 w-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                            }
                        </tbody>
                    </table>
                </div>

                {/* ── Pagination ── */}
                {!loading && leads.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-ink-100 bg-ink-50 flex-wrap gap-2">
                        <div className="text-xs text-ink-500">
                            Showing <b>{(page - 1) * perPage + 1}–{Math.min(page * perPage, total)}</b> of <b>{total.toLocaleString()}</b>
                        </div>
                        <div className="flex items-center gap-1">
                            <button className="ag-btn-ghost py-1 px-2" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                const p = Math.max(1, Math.min(page - 3, totalPages - 6)) + i;
                                return p <= totalPages ? (
                                    <button
                                        key={p}
                                        onClick={() => setPage(p)}
                                        className={`h-7 min-w-[28px] px-1.5 rounded text-xs font-medium transition
                      ${page === p ? 'bg-brand-teal text-white' : 'hover:bg-ink-100 text-ink-700'}`}
                                    >
                                        {p}
                                    </button>
                                ) : null;
                            })}
                            <button className="ag-btn-ghost py-1 px-2" disabled={!hasMore && page >= totalPages} onClick={() => setPage(p => p + 1)}>
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Column Picker ── */}
            {showPicker && (
                <ColumnPicker
                    allKeys={allKeys}
                    columns={columns}
                    onChange={setColumns}
                    onClose={() => setShowPicker(false)}
                />
            )}

            {/* ── Export Modal ── */}
            {showExport && (
                <ExportModal
                    onClose={() => setShowExport(false)}
                    onExportCurrentCSV={() => exportCSV(leads, columns)}
                />
            )}

            {/* ── Lead Drawer ── */}
            <LeadDrawer lead={selected} onClose={() => setSelected(null)} />
        </div>
    );
}